// src/components/ProductList/components/LeftoverItem.js
import React from "react";
import { useTranslation } from "react-i18next";
import {
  getLocalizedProductName,
  getProductImage,
} from "../utils/productListUtils";

const getFallbackValue = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

/**
 * Leftover card — renders a single leftover ingredient entry inside the
 * "Leftovers" tab. Pure presentational component; only side-effect is
 * invoking `onPreview` when the thumbnail is clicked.
 */
const LeftoverItem = ({ item, onPreview }) => {
  const { t, i18n } = useTranslation("mealplan");

  const productName = getLocalizedProductName(item, i18n.language);
  const leftoverAmount = getFallbackValue(item.leftoverAmount, "");

  const productForImage = {
    productName,
    productId: item.productId,
    selectedSize: item.selectedSize,
  };
  const productImage = getProductImage(productForImage);

  return (
    <div className="bg-[#fff] p-4 border border-b-[var(--Color-Grays-Gray-3,#E8E6E3)] border-[0.5px]">
      <div className="flex items-start gap-3 mb-2">
        <div
          className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
          onClick={() =>
            onPreview && onPreview({ src: productImage, name: productName })
          }
        >
          <img
            src={productImage}
            alt={productName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "/assets/products/no-image.jpg";
            }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-0">
            <h4 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[24px] flex-1">
              {productName}
            </h4>
            {item.perishable && (
              <span className="text-[10px] bg-[#FEF3C7] text-[#92400E] px-2 py-1 rounded-[4px] font-medium ml-2">
                🕒 {t("grocery.perishable")}
              </span>
            )}
          </div>
          <p className="text-[var(--Color-Grays-Gray-7,#353227)] text-[12px] font-medium leading-[20px] mb-2">
            {leftoverAmount}
            {item.leftoverPercentage
              ? ` (${item.leftoverPercentage} ${t("grocery.ofPackage")})`
              : ""}
          </p>
          <p className="inline text-[var(--Color-Grays-Gray-7,#353227)] text-[12px] leading-[20px] px-2 py-1.5 rounded-[8px] bg-[var(--Color-Grays-Gray-2,#F5F3F0)]">
            {item.category || t("grocery.uncategorized")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(LeftoverItem);
